import { createContext, useContext, useEffect, useRef, useState } from "react";
import { useCookies, Cookies } from 'react-cookie';
import axios from 'axios';

const UserContext = createContext();

const REMEMBER_FOR = 60 * 60 * 24 * 30; // matches the API token lifetime (30 days)

// Every axios call sends the login token; set synchronously so the very next request has it
const setAuthHeader = (token) => {
    if (token) axios.defaults.headers.common.Authorization = `Bearer ${token}`;
    else delete axios.defaults.headers.common.Authorization;
};

export const UserProvider = ({ children }) => {

    const [cookies, setCookie, removeCookie] = useCookies(['currentUser', 'authToken']);
    // Sessions from before tokens existed (id cookie only) are dropped: the user logs in again.
    // Their cart cookie belonged to that account, so drop it too; otherwise it would be merged
    // into whoever logs in next on this device. (Runs before CartProvider reads the cookie.)
    const [session, setSession] = useState(() => {
        const valid = cookies.currentUser && cookies.authToken;
        if (cookies.currentUser && !cookies.authToken) new Cookies().remove('cart', { path: '/' });
        setAuthHeader(valid ? cookies.authToken : null);
        return valid ? { userId: cookies.currentUser, token: cookies.authToken } : { userId: '', token: '' };
    });

    useEffect(() => {
        if (cookies.currentUser && !cookies.authToken) removeCookie('currentUser', { path: '/' });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // `remember` keeps the session for 30 days; otherwise it ends when the browser closes
    const login = (user, token, remember = true) => {
        const options = remember ? { path: '/', maxAge: REMEMBER_FOR } : { path: '/' };
        setCookie('authToken', token, options);
        setCookie('currentUser', user._id, options);
        setAuthHeader(token);
        setSession({ userId: user._id, token });
    };

    const logout = () => {
        removeCookie('authToken', { path: '/' });
        removeCookie('currentUser', { path: '/' });
        setAuthHeader(null);
        setSession({ userId: '', token: '' });
    };

    // An expired/invalid token anywhere logs the user out instead of failing silently
    const logoutRef = useRef(logout);
    logoutRef.current = logout;
    useEffect(() => {
        const id = axios.interceptors.response.use(undefined, (error) => {
            if (error.response?.status === 401 && axios.defaults.headers.common.Authorization) logoutRef.current();
            return Promise.reject(error);
        });
        return () => axios.interceptors.response.eject(id);
    }, []);

    const values = {
        currentUser: session.userId,
        token: session.token,
        login,
        logout
    };

    return <UserContext.Provider value={values}>{children}</UserContext.Provider>
};

export const useUserContext = () => useContext(UserContext);
